import classNames from 'classnames'
import dateFormat from 'dateformat'
import React, { ReactNode, useEffect, useRef, useState } from 'react'
import SimpleBar from 'simplebar-react'
import 'simplebar-react/dist/simplebar.min.css'

import { Portal } from '@components/Portal'
import { StatusItem } from '@components/StatusItem'

import { Button } from '@UI/Button'
import { ActionError } from '@UI/ConstructElements'
import { Loader } from '@UI/Loader'

import { useAnimate } from '@hooks/useAnimate'
import { useCall } from '@hooks/useCall'

import { useSearchByPhoneQuery } from '@/store/shop/orders/orders.api'
import { CallStatus } from '@/types'
import { ReactComponent as ArrowIcon } from '@assets/icons/arrow.svg'
import { ReactComponent as EndCallIcon } from '@assets/icons/end-call.svg'

import s from './CallModal.module.scss'
import type { CallModalProps } from './CallModal.props'

const statuses: Record<CallStatus, string> = {
  connected: "З'єднання встановлено",
  ended: 'Дзвінок завершено',
  failed: 'Збій дзвінка',
  incoming: 'Абонент чекає',
  outgoing: 'Чекання відповіді',
  paused: 'Дзвінок на паузі',
  unpaused: 'Дзвінок відновлення',
  '': '',
}

export function CallModal({}: CallModalProps) {
  const bodyStyles = useRef(document.body.style)

  const { status, isIncoming, target, closeModal, makeCall, answerCall, endCall, holdCall, unholdCall } = useCall()

  const { data, isLoading, error } = useSearchByPhoneQuery({ phone: target }, { skip: !target })

  const [minimize, setMinimize] = useState(false)
  const [isAnimate, closeHandler] = useAnimate(closeModal)

  const RenderButton = () => {
    switch (status) {
      case 'connected':
        return <Button onClick={holdCall}>Удержание</Button>
      case 'paused':
        return <Button onClick={unholdCall}>Возобновить</Button>
      case 'incoming':
        return <Button onClick={answerCall}>Ответить</Button>
      case '':
      case 'outgoing':
      case 'ended':
      case 'failed':
        return (
          <Button
            onClick={makeCall}
            disabled={status === 'outgoing'}
          >
            Позвонить
          </Button>
        )
      default:
        return null
    }
  }

  const lockWindowScroll = () => {
    document.body.style.overflowY = 'hidden'
  }

  const unlockWindowScroll = () => {
    if (bodyStyles.current.overflowY !== 'hidden') {
      document.body.style.overflowY = 'auto'
    }
  }

  useEffect(() => {
    lockWindowScroll()

    return () => {
      unlockWindowScroll()
    }
  }, [])

  return (
    <Portal>
      <div
        className={classNames(s.overlay, {
          [s.animate]: isAnimate,
          [s.minimize]: minimize,
        })}
        onClick={() => ['ended', 'failed', ''].includes(status) && closeHandler()}
      />
      <div
        className={classNames(s.modal, {
          [s.animate]: isAnimate,
          [s.minimize]: minimize,
        })}
      >
        {!minimize && (
          <div className={s.modalHeader}>
            <h2 className={s.modalTitle}>
              Звонок {isIncoming ? 'от' : 'на'} {target}
            </h2>

            {!isIncoming && status && (
              <div
                className={s.minimize}
                onClick={() => setMinimize(true)}
              >
                Свернуть
              </div>
            )}
          </div>
        )}

        {isLoading && <Loader />}
        {error && 'data' in error && <ActionError>{error?.data?.message}</ActionError>}
        {!isLoading && !error && !data?.phoneForMobileDialing && <ActionError>Invalid phone number</ActionError>}
        {!isLoading && !error && data?.phoneForMobileDialing && (
          <>
            {!minimize && (
              <>
                {data.client?.name && (
                  <>
                    <h4 className={s.title}>Клиент</h4>
                    <div className={s.userWrapper}>
                      {data.client?.avatar && (
                        <img
                          src={data.client.avatar}
                          alt="avatar"
                          className={s.avatar}
                        />
                      )}
                      <div className={s.name}>{data.client.name}</div>
                    </div>
                    <hr className={s.hr} />
                  </>
                )}
                {data.responsible?.name && (
                  <>
                    <h4 className={s.title}>Ответственный</h4>
                    <div className={s.userWrapper}>
                      {data.responsible?.avatar && (
                        <img
                          src={data.responsible.avatar}
                          alt="avatar"
                          className={s.avatar}
                        />
                      )}
                      <div className={s.name}>{data.responsible.name}</div>
                    </div>
                    <hr className={s.hr} />
                  </>
                )}
                {!!data.orders?.length && (
                  <>
                    <h4 className={s.title}>Заказы</h4>
                    <SimpleBar style={{ maxHeight: '200px', height: '100%' }}>
                      <ul className={s.list}>
                        {data.orders.map((order) => (
                          <li
                            key={order.id}
                            className={s.listItem}
                          >
                            <div className={s.listItemContent}>
                              Заказ №{order.id} от {dateFormat(order.createdAt, 'dd.mm.yyyy - HH:MM')}
                              <div className={s.price}>{order.total}</div>
                            </div>
                            <StatusItem
                              title={order.orderStatus.name}
                              style={{ borderBottom: `1px solid ${order.orderStatus.backgroundColor}` }}
                            />
                          </li>
                        ))}
                      </ul>
                    </SimpleBar>
                    <hr className={s.hr} />
                  </>
                )}
              </>
            )}

            {!minimize && <div className={s.status}>{statuses[status]}</div>}

            <div className={s.btnsWrapper}>
              {!minimize && <RenderButton />}

              {status && !['ended', 'failed'].includes(status) && <EndCallButton onClick={endCall}>{status === 'incoming' ? 'Отклонить' : 'Завершить'}</EndCallButton>}

              {minimize && (
                <div
                  onClick={() => setMinimize(false)}
                  className={s.unminimize}
                >
                  <ArrowIcon />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Portal>
  )
}

function EndCallButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <Button
      onClick={onClick}
      className={s.endBtn}
    >
      <EndCallIcon />
      {children}
    </Button>
  )
}
